import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingActivity, setEditingActivity] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    date: new Date().toISOString().split('T')[0],
    status: 'published',
    image: null
  });

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const data = await adminApi.getActivities();
      setActivities(data);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity.id);
    setFormData({
      title: activity.title,
      content: activity.content,
      date: activity.date,
      status: activity.status,
      image: activity.image
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingActivity(null);
    setFormData({
      title: '',
      content: '',
      date: new Date().toISOString().split('T')[0],
      status: 'published',
      image: null
    });
  };

  const handleCancel = () => {
    setEditingActivity(null);
    setIsCreating(false);
    setFormData({ title: '', content: '', date: new Date().toISOString().split('T')[0], status: 'published', image: null });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const result = await adminApi.uploadActivityImage(file);
      setFormData({ ...formData, image: result.path });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.content || !formData.date) {
        toast.error('Please fill all required fields');
        return;
      }

      if (isCreating) {
        await adminApi.createActivity(formData);
        toast.success('Activity created successfully');
      } else {
        await adminApi.updateActivity(editingActivity, formData);
        toast.success('Activity updated successfully');
      }

      loadActivities();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save activity');
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await adminApi.deleteActivity(activityId);
      toast.success('Activity deleted successfully');
      loadActivities();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  if (loading) {
    return (
      <AdminDashboard>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading activities...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Activities / News</h1>
            <p className="text-slate-600 mt-1">Manage your company news and ongoing activities</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Activity
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingActivity) && (
          <Card className="border-2 border-[#1E4E9E]">
            <CardHeader>
              <CardTitle>{isCreating ? 'Create New Activity' : 'Edit Activity'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Activity title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Content *</label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your news content here..."
                  rows={6}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Date *</label>
                  <Input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="December 2025"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:border-[#1E4E9E] focus:ring-[#1E4E9E]"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Image</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer">
                    <div className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#1E4E9E] transition-colors">
                      <Upload className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {formData.image && (
                    <div className="flex items-center space-x-2">
                      <ImageIcon className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">Image uploaded</span>
                    </div>
                  )}
                </div>
                {formData.image && (
                  <img
                    src={`${BACKEND_URL}${formData.image}`}
                    alt="Preview"
                    className="mt-4 w-64 h-40 object-cover rounded-lg border"
                  />
                )}
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleSave} className="bg-[#00B050] hover:bg-[#00B050]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activities List */}
        <div className="grid md:grid-cols-2 gap-6">
          {activities.map((activity) => (
            <Card key={activity.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl">{activity.title}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{activity.date}</p>
                    <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      activity.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(activity)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(activity.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {activity.image && (
                  <img
                    src={`${BACKEND_URL}${activity.image}`}
                    alt={activity.title}
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                )}
                <p className="text-slate-600 text-sm line-clamp-3">{activity.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminDashboard>
  );
};
